/**
 * Theme for the Autocomplete Component.
 */
import React from 'react';

import { components, IndicatorProps, Styles, StylesConfig, Theme } from 'react-select';
import styled from 'styled-components/native';

import { Colors } from 'theme/colors';
import { FontSize, FontWeight } from 'theme/fonts';
import { NakedIcon } from 'theme/icons';
import { SelectComponents } from 'react-select/src/components';

const LINE_HEIGHT = '34px';
const BOTTOM_BORDER_RADIUS = '7px';
const TEXT_SIZE = FontSize.Regular;
const HORIZONTAL_PADDING = '10px';
const HORIZONTAL_PADDING_LEFT = '30px';

/**
 * The theme and the Styles have to be set like this since
 * react-select v2 does not generate predictable classnames
 * which can be overridden in styled-components
 * unless a specific class name prefix is supplied.
 *
 * @param theme - Autocomplete base theme definition.
 */
export const AutocompleteTheme = (theme: Theme) => ({
  colors: {
    ...theme.colors,
    primary: Colors.green,
    primary25: Colors.green,
  },
  borderRadius: 7,
  spacing: theme.spacing,
});

export const AutocompleteStyles: StylesConfig = {
  menuList: provided => ({
    ...provided,
    paddingTop: '0px',
    maxHeight: '200px',
  }),
  clearIndicator: provided => ({
    ...provided,
    marginRight: '16px',
  }),
  control: provided => ({
    ...provided,
    boxShadow: 'none',
    marginBottom: '0px',
    borderWidth: '1px',
  }),
  container: provided => ({
    ...provided,
    width: '100%',
    lineHeight: LINE_HEIGHT,
  }),
  dropdownIndicator: previous => ({
    ...previous,
    color: Colors.charcoal,
    marginRight: '16px',
  }),
  indicatorSeparator: provided => ({
    ...provided,
    display: 'none',
  }),
  menu: provided => ({
    ...provided,
    top: '45px',
    boxShadow: 'none',
    border: `1px solid ${Colors.green}`,
    borderRadius: '7px',
    borderBottomRightRadius: BOTTOM_BORDER_RADIUS,
    borderBottomLeftRadius: BOTTOM_BORDER_RADIUS,
    maxHeight: '200px',
    overflow: 'hidden',
  }),
  option: (provided, state) => ({
    ...provided,
    color: Colors.charcoal,
    fontSize: TEXT_SIZE,
    fontFamily: 'Roboto',
    fontWeight: (state.isSelected ? FontWeight.Regular : FontWeight.Light) as never,
    backgroundColor: Colors.white,
    paddingLeft: '20px',

    '&:hover': {
      ...provided,
      color: Colors.white,
      backgroundColor: Colors.green,
      fontSize: TEXT_SIZE,
      paddingLeft: '20px',
    },
    '&:nth-of-type(odd)': {
      ...provided,
      backgroundColor: Colors.alabastar,
      color: Colors.charcoal,
      fontSize: TEXT_SIZE,
      paddingLeft: '20px',
    },
    '&:nth-of-type(odd):hover': {
      ...provided,
      color: Colors.white,
      backgroundColor: Colors.green,
      fontSize: TEXT_SIZE,
      paddingLeft: '20px',
    },
  }),
  noOptionsMessage: provided => ({
    ...provided,
    textAlign: 'left',
    lineHeight: LINE_HEIGHT,
    maxHeight: '45px',
    fontFamily: 'Roboto',
    fontWeight: FontWeight.Light as never,
    fontSize: TEXT_SIZE,
    color: Colors.nobel,
    paddingLeft: '20px',
  }),
};
/**
 * Returns the styles needed to override the autocomplete's accent color and text that displays on top of the accent color
 *
 * @param baseAutoCompleteStyles - The base styles used to style the autocomplete
 * @param accentColor - What the new accent color should be
 * @param accentTextColor - What the new color should be for text that is shown on top of the accentcolor
 */
export function getColorOverrideStyles(
  baseAutoCompleteStyles: Partial<Styles>,
  accentColor?: string,
  accentTextColor?: string
) {
  const stylesWithColorOverrides: Partial<Styles> = {
    ...baseAutoCompleteStyles,
    menu: (base, props) => {
      const originalStyles = baseAutoCompleteStyles.menu?.(base, props);
      return {
        ...originalStyles,
        borderColor: accentColor,
      };
    },
    option: (base, props) => {
      const originalStyles = baseAutoCompleteStyles.option?.(base, props);
      return {
        ...originalStyles,
        '&:hover': {
          ...originalStyles['&:hover'],
          backgroundColor: accentColor,
          color: accentTextColor,
        },
        '&:nth-of-type(odd):hover': {
          ...originalStyles['&:nth-of-type(odd):hover'],
          backgroundColor: accentColor,
          color: accentTextColor,
        },
      };
    },
  };

  return stylesWithColorOverrides;
}
/**
 * Returns the theme config needed to override the autocomplete's accent color
 *
 * @param baseAutoCompleteTheme - The base theme config used to style the autocomplete
 * @param accentColor - What the new accent color should be
 */
export function getColorOverrideTheme(
  baseAutoCompleteTheme: typeof AutocompleteTheme,
  accentColor?: string
) {
  return (theme: Theme) => {
    const originalTheme = baseAutoCompleteTheme(theme);

    return {
      ...originalTheme,
      colors: {
        ...originalTheme.colors,
        primary: accentColor,
        primary25: accentColor,
      },
    };
  };
}
export const PreIconContainer = styled.View`
  position: absolute;
  width: 12px;
  justify-content: center;
  margin: auto;
  top: 0;
  left: 14px;
  bottom: 0;
  z-index: 1;
`;

/**
 * Dropdown indicator for vehicle selector
 *
 * @param props - Render props
 */
function DropdownIndicator(props: CustomIndicatorProps) {
  return (
    <components.DropdownIndicator {...props}>
      <NakedIcon name="arrow-down" size={10} />
    </components.DropdownIndicator>
  );
}
/**
 * Clear Indicator for vehicle selector
 *
 * @param props - Render props
 */
function ClearIndicator(props: CustomIndicatorProps) {
  return (
    <components.ClearIndicator {...props}>
      <NakedIcon name="close" size={10} />
    </components.ClearIndicator>
  );
}
/**
 * returns the styles for showing dropdown indicator and autocomplete specific styles
 *
 * @param icon - the icon to be shown on the left of the filed.
 * @param showDropdownIndicator - whether dropdown indicator should show.
 */
export function getAutocompleteStyles(icon?: string, showDropdownIndicator = true) {
  /* show the dropdown indicator by default, otherwise use explicit instruction from prop */
  const inputStyles = { paddingLeft: icon ? HORIZONTAL_PADDING_LEFT : HORIZONTAL_PADDING };
  const styledComponents: StyledComponents = {
    ClearIndicator,
  };

  styledComponents.DropdownIndicator = showDropdownIndicator ? DropdownIndicator : () => null;

  const autoCompleteStyles: StylesConfig = {
    ...AutocompleteStyles,
    input: provided => ({
      ...provided,
      paddingRight: HORIZONTAL_PADDING,
      fontFamily: 'Roboto',
      fontSize: TEXT_SIZE,
      lineHeight: LINE_HEIGHT,
      color: Colors.black,
      fontWeight: FontWeight.Light as never,
      ...inputStyles,
    }),
    placeholder: provided => ({
      ...provided,
      paddingRight: HORIZONTAL_PADDING,
      fontFamily: 'Roboto',
      fontSize: TEXT_SIZE,
      lineHeight: LINE_HEIGHT,
      fontWeight: FontWeight.Light as never,
      color: Colors.stack,
      ...inputStyles,
    }),
    singleValue: provided => ({
      ...provided,
      ...inputStyles,
    }),
  };

  return {
    styledComponents,
    autoCompleteStyles,
  };
}
/* Type Definitions */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type CustomIndicatorProps = IndicatorProps<any, boolean>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type StyledComponents = Partial<SelectComponents<any, boolean>>;
export interface IconProps {
  /** Indicator for whether a field is editable */
  icon?: string;
}
